import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  IconButton,
  Stack,
  type SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { useCallback } from 'react';
import { Controller, type UseFormReturn, useWatch } from 'react-hook-form';
import { IconGridSelector, Tooltip } from 'src/components';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import type { ConversationAssigneeGroupManagement } from 'src/graphql/entities/conversation-assignee-group';
import { ASSIGNEE_GROUP_AVAILABLE_ICONS } from './constants';
import {
  ASSIGNEE_GROUP_DESCRIPTION_MAX_LENGTH,
  ASSIGNEE_GROUP_NAME_ALLOWED_CHARS_REGEX,
  ASSIGNEE_GROUP_NAME_ERRORS,
  ASSIGNEE_GROUP_NAME_MAX_LENGTH,
  validateAssigneeGroupName,
} from './validators';

export enum AssigneeGroupFormField {
  Name = 'name',
  Description = 'description',
  Icon = 'icon',
}

export interface AssigneeGroupFormValues {
  [AssigneeGroupFormField.Name]: string;
  [AssigneeGroupFormField.Description]: string;
  [AssigneeGroupFormField.Icon]: string;
}

export const EMPTY_ASSIGNEE_GROUP_FORM: AssigneeGroupFormValues = {
  [AssigneeGroupFormField.Name]: '',
  [AssigneeGroupFormField.Description]: '',
  [AssigneeGroupFormField.Icon]: '',
};

export function getAssigneeGroupDefaults(
  group?: ConversationAssigneeGroupManagement | null,
): AssigneeGroupFormValues {
  if (!group) return EMPTY_ASSIGNEE_GROUP_FORM;
  return {
    [AssigneeGroupFormField.Name]: group.name ?? '',
    [AssigneeGroupFormField.Description]: group.description ?? '',
    [AssigneeGroupFormField.Icon]: group.icon ?? '',
  };
}

const removeIconBadgeSx: SxProps<Theme> = (theme) => ({
  position: 'absolute',
  right: -5,
  top: -5,
  width: 14,
  height: 14,
  padding: 0,
  bgcolor: theme.palette.background.paper,
  color: theme.palette.custom.OV.sys.outline,
  border: `1px solid ${theme.palette.custom.OV.sys.outline}`,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
});

const iconWrapperSx: SxProps<Theme> = {
  position: 'relative',
  display: 'inline-flex',
  alignSelf: 'flex-start',
};

const descriptionCountSx: SxProps<Theme> = {
  display: 'block',
  textAlign: 'right',
  mt: 0.25,
};

interface UseAssigneeGroupFormProps {
  formMethods: UseFormReturn<AssigneeGroupFormValues>;
  partnerId: number | null;
  existingGroups: ConversationAssigneeGroupManagement[];
  editingId?: string;
}

export function useAssigneeGroupForm({
  formMethods,
  partnerId,
  existingGroups,
  editingId,
}: UseAssigneeGroupFormProps) {
  const { control, setValue } = formMethods;
  const iconValue = useWatch({ control, name: AssigneeGroupFormField.Icon });
  const descriptionValue =
    useWatch({ control, name: AssigneeGroupFormField.Description }) ?? '';

  const validateUniqueName = useCallback(
    (value: string) => {
      const err = validateAssigneeGroupName({
        name: value,
        partnerId,
        existingGroups,
        editingId,
      });
      return err ?? true;
    },
    [partnerId, existingGroups, editingId],
  );

  return (
    <Stack spacing={2}>
      <Box sx={iconWrapperSx}>
        <Controller
          control={control}
          name={AssigneeGroupFormField.Icon}
          render={({ field }) => (
            <IconGridSelector
              icons={ASSIGNEE_GROUP_AVAILABLE_ICONS}
              library="lucide"
              value={field.value}
              onChange={field.onChange}
              placeholderIcon="add_reaction"
              tooltipLabel="Choose an icon"
            />
          )}
        />
        {iconValue ? (
          <Tooltip title="Remove icon">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setValue(AssigneeGroupFormField.Icon, '', { shouldDirty: true });
              }}
              sx={removeIconBadgeSx}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      {/*
        Name uses Controller + MUI TextField directly
        because uniqueness validation needs `rules.validate` (depends on the
        external `existingGroups` list), and `FormTextField` does not expose
        that rule.
      */}
      <Controller
        control={control}
        name={AssigneeGroupFormField.Name}
        rules={{
          required: ASSIGNEE_GROUP_NAME_ERRORS.Required,
          pattern: {
            value: ASSIGNEE_GROUP_NAME_ALLOWED_CHARS_REGEX,
            message: ASSIGNEE_GROUP_NAME_ERRORS.InvalidChars,
          },
          validate: validateUniqueName,
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Name"
            variant="outlined"
            size="small"
            required
            autoFocus
            fullWidth
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            inputProps={{
              maxLength: ASSIGNEE_GROUP_NAME_MAX_LENGTH,
              'data-testid': 'manage-assignee-group-name',
            }}
          />
        )}
      />

      <Box>
        <FormTextField
          type="textfield"
          name={AssigneeGroupFormField.Description}
          label="Description"
          variant="outlined"
          showErrorText
          fullWidth
          textFieldProps={{
            size: 'small',
            multiline: true,
            minRows: 2,
            maxRows: 5,
            fullWidth: true,
            inputProps: {
              maxLength: ASSIGNEE_GROUP_DESCRIPTION_MAX_LENGTH,
              'data-testid': 'manage-assignee-group-description',
            },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={descriptionCountSx}>
          {descriptionValue.length}/{ASSIGNEE_GROUP_DESCRIPTION_MAX_LENGTH}
        </Typography>
      </Box>
    </Stack>
  );
}
